import React from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { adminTokens as T } from '../../../theme/adminTokens';

export interface TrendSeries {
  key: string;
  label: string;
  color?: string;
}

export interface TrendChartProps {
  title: string;
  subtitle?: string;
  data: Array<Record<string, any>>;
  series: TrendSeries[];
  xKey?: string;
  loading?: boolean;
  height?: number;
  empty?: boolean;
  emptyMessage?: string;
  rightSlot?: React.ReactNode;
}

const TrendChart: React.FC<TrendChartProps> = ({
  title, subtitle, data, series, xKey = 'date',
  loading, height = 280, empty, emptyMessage = 'No data for the selected range',
  rightSlot,
}) => {
  return (
    <Box sx={{
      bgcolor: T.color.surface.raised,
      border: `1px solid ${T.color.surface.border}`,
      borderRadius: `${T.radius.lg}px`,
      p: `${T.space[5]}px`,
      boxShadow: T.shadow.sm,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: `${T.space[4]}px` }}>
        <Box>
          <Typography sx={{ fontSize: T.type.size.lg, fontWeight: T.type.weight.semibold, color: T.color.text.primary, lineHeight: T.type.lineHeight.tight }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: T.type.size.sm, color: T.color.text.secondary, mt: `${T.space[1]}px` }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {rightSlot}
      </Box>

      {loading ? (
        <Skeleton variant="rectangular" height={height} sx={{ borderRadius: `${T.radius.md}px` }} />
      ) : empty || !data?.length ? (
        <Box sx={{
          height, display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: T.color.text.tertiary, fontSize: T.type.size.sm,
          border: `1px dashed ${T.color.surface.border}`, borderRadius: `${T.radius.md}px`,
        }}>
          {emptyMessage}
        </Box>
      ) : (
        <Box sx={{ height, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
              <defs>
                {series.map((s, i) => {
                  const color = s.color ?? T.color.chart[i % T.color.chart.length];
                  return (
                    <linearGradient key={s.key} id={`gradient-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor={color} stopOpacity={0.28} />
                      <stop offset="100%" stopColor={color} stopOpacity={0} />
                    </linearGradient>
                  );
                })}
              </defs>
              <CartesianGrid strokeDasharray="4 4" stroke={T.color.surface.border} vertical={false} />
              <XAxis
                dataKey={xKey}
                tick={{ fontSize: T.type.size.xs, fill: T.color.text.tertiary }}
                tickLine={false}
                axisLine={{ stroke: T.color.surface.border }}
              />
              <YAxis
                tick={{ fontSize: T.type.size.xs, fill: T.color.text.tertiary }}
                tickLine={false}
                axisLine={false}
                width={48}
              />
              <Tooltip
                contentStyle={{
                  background: T.color.surface.raised,
                  border: `1px solid ${T.color.surface.border}`,
                  borderRadius: T.radius.md,
                  boxShadow: T.shadow.md,
                  fontSize: T.type.size.sm,
                }}
                labelStyle={{ color: T.color.text.secondary, fontWeight: T.type.weight.medium }}
              />
              {series.length > 1 && (
                <Legend
                  iconType="circle"
                  wrapperStyle={{ fontSize: T.type.size.sm, color: T.color.text.secondary, paddingTop: T.space[2] }}
                />
              )}
              {series.map((s, i) => {
                const color = s.color ?? T.color.chart[i % T.color.chart.length];
                return (
                  <Area
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={s.label}
                    stroke={color}
                    strokeWidth={2}
                    fill={`url(#gradient-${s.key})`}
                    activeDot={{ r: 4, strokeWidth: 2 }}
                  />
                );
              })}
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
};

export default TrendChart;
